import React, { ReactElement, useState } from 'react';
import { Team } from '@enview/interface/types/Team';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import WorkspaceSelectorContent from './WorkspaceSelectorContent';
import './WorkspacePicker.scss';

export type WorkspacePickerMode = 'workspaceAssignment' | 'actionScope';

interface WorkspacePickerProps {
  trigger: ReactElement;
  /** Every workspace the current user belongs to */
  workspaces: Team[];
  billWorkspaceIds: number[];
  onSave: (workspaceIds: number[]) => void | Promise<void>;
  sectionLabel?: string;
  /**
   * `workspaceAssignment` (default) shows all of the user's workspaces and permits
   * add/remove — this is bill workspace membership itself.
   * `actionScope` scopes an action (Priority, Workflow Status, Assignee, Tracking) to
   * a workspace the bill is already in: with no bill workspaces it behaves like
   * workspaceAssignment (there's nothing to scope to yet); with exactly one, only
   * the trigger renders and it opens nothing; with two or more, it's restricted to
   * the bill's existing workspaces and can't change membership.
   */
  mode?: WorkspacePickerMode;
  /**
   * Opens the picker programmatically (e.g. as a gate after another action was
   * attempted on a bill that isn't in a workspace yet), independent of the
   * trigger's own click. Ignored by consumers that don't pass it.
   */
  forceOpen?: boolean;
  /** Called when the picker closes while forceOpen was driving it open. */
  onForceClose?: () => void;
  /** Disables Save while no workspace is selected, for callers where an empty selection isn't a valid outcome */
  requireSelection?: boolean;
  /** Disables Save while a previous save from this picker is still in flight, to prevent duplicate submissions */
  saving?: boolean;
}

const WorkspacePicker = (props: WorkspacePickerProps): ReactElement => {
  const {
    trigger,
    workspaces,
    billWorkspaceIds,
    onSave,
    sectionLabel = 'ADD TO WORKSPACE',
    mode = 'workspaceAssignment',
    forceOpen = false,
    onForceClose,
    requireSelection = false,
    saving = false,
  } = props;

  const [internalShow, setInternalShow] = useState(false);
  const show = internalShow || forceOpen;

  // The bill's workspaces that the user belongs to. actionScope mode only offers or
  // resolves to these, never a bill workspace the user isn't a member of.
  const billUserWorkspaces = workspaces.filter((workspace) =>
    billWorkspaceIds.includes(workspace.id),
  );
  const restricted = mode === 'actionScope' && billUserWorkspaces.length >= 2;

  const handleToggle = (): void => {
    setInternalShow((prevShow) => !prevShow);
  };

  const handleClose = (): void => {
    setInternalShow(false);
    if (forceOpen) onForceClose?.();
  };

  const handleSave = async (workspaceIds: number[]): Promise<void> => {
    await onSave(workspaceIds);
    handleClose();
  };

  // With exactly one bill workspace there is nothing to choose between, so actionScope
  // renders only the trigger and the caller applies the action to that workspace.
  if (mode === 'actionScope' && billUserWorkspaces.length === 1) return trigger;

  const options = restricted ? billUserWorkspaces : workspaces;

  return (
    <ConfirmActionPopover
      align="start"
      className="workspace-picker-popover"
      closePopup={handleClose}
      content={
        show ? (
          <WorkspaceSelectorContent
            billWorkspaceIds={billWorkspaceIds}
            onSave={handleSave}
            requireSelection={requireSelection}
            restricted={restricted}
            saving={saving}
            sectionLabel={sectionLabel}
            workspaces={options}
          />
        ) : null
      }
      hideCloseIcon
      onClickOutside={handleClose}
      positions={['bottom', 'top', 'right', 'left']}
      show={show}
      showArrowContainer={false}
      target={trigger}
      toggleCallback={handleToggle}
    />
  );
};

export default WorkspacePicker;
